import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  FolderOpen,
  Search,
  ChevronLeft,
  Image,
  Music,
  FileVideo,
  FileText,
  Loader2
} from 'lucide-react'
import { cn } from '@/lib/utils'

const fileTypeIcons: Record<string, React.ElementType> = {
  image: Image,
  audio: Music,
  video: FileVideo,
  document: FileText
}

export default function ResourcesList() {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [materials, setMaterials] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      const { data } = await supabase
        .from('content_materials')
        .select('*, content:contents(id, title)')
        .order('created_at', { ascending: false })
      if (data) setMaterials(data)
      setLoading(false)
    }

    loadData()
  }, [])

  const filteredMaterials = materials.filter(m => {
    const matchesSearch = (m.title || m.file_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = typeFilter === 'all' || m.material_type === typeFilter
    return matchesSearch && matchesType
  })

  const materialTypes = [...new Set(materials.map(m => m.material_type).filter(Boolean))]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/teacher/content" className="p-2 hover:bg-muted rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Recursos</h1>
          <p className="text-muted-foreground">
            {`${materials.length} recursos disponibles (solo lectura)`}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background"
          />
        </div>
        {materialTypes.length > 0 && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-background"
          >
            <option value="all">Todos los tipos</option>
            {materialTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        )}
      </div>

      {/* Lista */}
      <div className="grid gap-3">
        {filteredMaterials.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">No se encontraron recursos</p>
            <p className="text-sm text-muted-foreground mt-1">
              Los recursos del curso aparecerán aquí
            </p>
          </div>
        ) : (
          filteredMaterials.map((material) => {
            const TypeIcon = fileTypeIcons[material.material_type] || FileText
            return (
              <div
                key={material.id}
                className="bg-card rounded-xl border p-4"
              >
                <div className="flex items-center gap-4">
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                    material.material_type === 'image' ? 'bg-pink-100' :
                    material.material_type === 'audio' ? 'bg-purple-100' :
                    material.material_type === 'video' ? 'bg-blue-100' :
                    'bg-gray-100'
                  )}>
                    <TypeIcon className={cn(
                      'w-6 h-6',
                      material.material_type === 'image' ? 'text-pink-600' :
                      material.material_type === 'audio' ? 'text-purple-600' :
                      material.material_type === 'video' ? 'text-blue-600' :
                      'text-gray-600'
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{material.title || material.file_name || 'Recurso'}</h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="px-2 py-0.5 bg-muted rounded">{material.material_type}</span>
                      {material.content?.title && (
                        <span className="px-2 py-0.5 bg-muted rounded">{material.content.title}</span>
                      )}
                    </div>
                  </div>
                  {material.file_url && (
                    <a
                      href={material.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm border rounded-lg hover:bg-muted transition-colors"
                    >
                      Ver
                    </a>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
